import { Box, Popover } from "@mui/material";
import { useRef, useState } from "react";
import { FilterChipDef, SearchFilters } from "./filterChipDefs";
import FilterPopoverContent from "./FilterPopovers";
import { ThemeMode } from "../../theme/ThemeContext";

interface FilterChipProps {
  readonly chipDef: FilterChipDef;
  readonly filters: SearchFilters;
  readonly onFiltersChange: (f: SearchFilters) => void;
  readonly mode: ThemeMode;
  readonly customerId?: string;
  readonly siteMap?: Map<string, string>;
}

export default function FilterChip({
  chipDef,
  filters,
  onFiltersChange,
  mode,
  customerId,
  siteMap,
}: FilterChipProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const chipRef = useRef<HTMLButtonElement>(null);

  const hasValue = chipDef.hasValue(filters);
  const displayValue = chipDef.getDisplayValue(filters, siteMap);

  const handleClick = () => {
    setAnchorEl(chipRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        ref={chipRef}
        component="button"
        onClick={handleClick}
        sx={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "1px",
          px: "10px",
          py: "5px",
          border: "1px solid",
          borderColor: hasValue ? "var(--accent)" : "var(--border-mid)",
          borderRadius: "20px",
          backgroundColor: hasValue ? "var(--accent-dim)" : "transparent",
          cursor: "pointer",
          whiteSpace: "nowrap",
          flexShrink: 0,
          transition: "border-color 150ms ease, background-color 150ms ease",
          "&:hover": {
            borderColor: hasValue ? "var(--accent)" : "var(--border-strong)",
            backgroundColor: hasValue ? "var(--accent-dim)" : "var(--bg-hover)",
          },
        }}
      >
        <Box
          component="span"
          sx={{
            fontSize: "0.625rem",
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: hasValue ? "var(--accent)" : "var(--text-tertiary)",
            lineHeight: 1,
            userSelect: "none",
          }}
        >
          {chipDef.label}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <Box
            component="span"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              color: hasValue ? "var(--text-primary)" : "var(--text-secondary)",
              lineHeight: 1,
              userSelect: "none",
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {displayValue}
          </Box>
          {/* Chevron-down icon */}
          <Box
            component="svg"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeLinejoin="round"
            sx={{
              width: 10,
              height: 10,
              color: hasValue ? "var(--accent)" : "var(--text-tertiary)",
              flexShrink: 0,
              transform: anchorEl ? "rotate(180deg)" : "none",
              transition: "transform 150ms ease",
            }}
          >
            <path d="M6 9l6 6 6-6" />
          </Box>
        </Box>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "var(--bg-raised)",
              border: "1px solid var(--border-mid)",
              borderRadius: "8px",
              boxShadow: mode === "dark"
                ? "0 8px 24px rgba(0,0,0,0.5)"
                : "0 8px 24px rgba(0,0,0,0.12)",
              color: "var(--text-primary)",
              mt: "4px",
            },
          },
        }}
      >
        <FilterPopoverContent
          chipDef={chipDef}
          filters={filters}
          onFiltersChange={onFiltersChange}
          onClose={handleClose}
          mode={mode}
          customerId={customerId}
        />
      </Popover>
    </>
  );
}
